// src/components/communications/MessageComposer.jsx
// Unified compose interface for SMS, Email, and Call notes
import React, { useState, useRef, useEffect } from 'react';
import { csrfHeadersFor } from '../../api';
import {
  MessageSquare, Mail, Phone, Send, Loader2, AlertCircle,
  Paperclip, X, Bold, Italic, Underline, Link, List, Image,
  ClipboardCheck
} from 'lucide-react';

// SMS character limit (standard SMS is 160, but we allow longer with segments)
const SMS_CHAR_WARNING = 160;
const SMS_CHAR_MAX = 1600;

export default function MessageComposer({
  clientId,
  clientName,
  clientPhone,
  clientEmail,
  mode,
  onModeChange,
  onSend,
  onLogOutcome,
  disabled
}) {
  // SMS state
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsAttachment, setSmsAttachment] = useState(null);
  const [smsAttachmentPreview, setSmsAttachmentPreview] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Email state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [uploadingEmailAttachment, setUploadingEmailAttachment] = useState(false);

  // Call log state
  const [callStatus, setCallStatus] = useState('completed');
  const [callNotes, setCallNotes] = useState('');
  const [callSending, setCallSending] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Refs
  const smsInputRef = useRef(null);
  const emailBodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const emailFileInputRef = useRef(null);

  // Focus input when mode changes
  useEffect(() => {
    if (mode === 'sms' && smsInputRef.current) {
      smsInputRef.current.focus();
    } else if (mode === 'email' && emailBodyRef.current) {
      emailBodyRef.current.focus();
    }
  }, [mode]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle file selection for MMS
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (images only for MMS)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG, PNG, and GIF images are supported for MMS');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setSmsAttachment(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSmsAttachmentPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove attachment
  const handleRemoveAttachment = () => {
    setSmsAttachment(null);
    setSmsAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload attachment to get public URL
  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/uploads/mms', {
      method: 'POST',
      credentials: 'include',
      headers: { ...csrfHeadersFor('POST') },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload image');
    }

    const result = await response.json();
    return result.data.url;
  };

  // Handle SMS send
  const handleSendSMS = async () => {
    if ((!smsMessage.trim() && !smsAttachment) || smsSending || disabled) return;

    setSmsSending(true);
    setError(null);

    try {
      let mediaUrl = null;

      // Upload attachment first if present
      if (smsAttachment) {
        setUploadingAttachment(true);
        try {
          const url = await uploadAttachment(smsAttachment);
          mediaUrl = [url]; // Twilio expects an array of URLs
        } catch (uploadErr) {
          setError(uploadErr.message || 'Failed to upload image');
          setSmsSending(false);
          setUploadingAttachment(false);
          return;
        }
        setUploadingAttachment(false);
      }

      const result = await onSend('sms', smsMessage.trim() || ' ', { mediaUrl });
      if (result.success) {
        setSmsMessage('');
        handleRemoveAttachment();
      } else {
        setError(result.error || 'Failed to send SMS');
      }
    } catch (err) {
      setError(err.message || 'Failed to send SMS');
    } finally {
      setSmsSending(false);
    }
  };

  // Handle email file selection
  const handleEmailFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv'
    ];

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`${file.name}: File type not allowed`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: File must be less than 10MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Upload each file
    setUploadingEmailAttachment(true);
    const newAttachments = [];

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/uploads/mms', {
          method: 'POST',
          credentials: 'include',
          headers: { ...csrfHeadersFor('POST') },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Upload failed');
        }

        const result = await response.json();
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: result.data.url
        });
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setEmailAttachments(prev => [...prev, ...newAttachments]);
    setUploadingEmailAttachment(false);

    // Clear the input
    if (emailFileInputRef.current) {
      emailFileInputRef.current.value = '';
    }
  };

  // Remove email attachment
  const handleRemoveEmailAttachment = (index) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Apply text formatting to email body
  const applyFormatting = (format) => {
    const textarea = emailBodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = emailBody.substring(start, end);

    let formattedText = '';
    let wrapper = { start: '', end: '' };

    switch (format) {
      case 'bold':
        wrapper = { start: '**', end: '**' };
        break;
      case 'italic':
        wrapper = { start: '_', end: '_' };
        break;
      case 'underline':
        wrapper = { start: '<u>', end: '</u>' };
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (url) {
          wrapper = { start: '[', end: `](${url})` };
        } else {
          return;
        }
        break;
      case 'list':
        wrapper = { start: '\n- ', end: '' };
        break;
      default:
        return;
    }

    formattedText = wrapper.start + selectedText + wrapper.end;
    const newBody = emailBody.substring(0, start) + formattedText + emailBody.substring(end);
    setEmailBody(newBody);

    // Restore focus and selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + wrapper.start.length, start + wrapper.start.length + selectedText.length);
    }, 0);
  };

  // Handle Email send
  const handleSendEmail = async () => {
    if (!emailBody.trim() || emailSending || disabled) return;

    setEmailSending(true);
    setError(null);

    try {
      // Prepare attachment URLs for the email
      const attachmentUrls = emailAttachments.map(a => a.url);

      const result = await onSend('email', emailBody.trim(), {
        subject: emailSubject.trim() || '(No subject)',
        attachments: attachmentUrls.length > 0 ? attachmentUrls : undefined
      });
      if (result.success) {
        setEmailSubject('');
        setEmailBody('');
        setEmailAttachments([]);
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      setError(err.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  // Handle Call log
  const handleLogCall = async () => {
    if (callSending || disabled) return;

    setCallSending(true);
    setError(null);

    try {
      const result = await onSend('call', callNotes.trim(), {
        metadata: {
          status: callStatus,
          logged_manually: true
        }
      });
      if (result.success) {
        setCallNotes('');
        setCallStatus('completed');
      } else {
        setError(result.error || 'Failed to log call');
      }
    } catch (err) {
      setError(err.message || 'Failed to log call');
    } finally {
      setCallSending(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e, sendFn) => {
    // Cmd/Ctrl + Enter to send
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      sendFn();
    }
  };

  // Calculate SMS segments
  const smsSegments = Math.ceil(smsMessage.length / 160) || 1;
  const smsCharRemaining = SMS_CHAR_MAX - smsMessage.length;

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Error Message */}
      {error && (
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mode Tabs */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <button
          onClick={() => onModeChange('sms')}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            mode === 'sms'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          <MessageSquare className="w-4 h-4" />
          SMS
        </button>
        <button
          onClick={() => onModeChange('email')}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            mode === 'email'
              ? 'bg-red-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          <Mail className="w-4 h-4" />
          Email
        </button>
        <button
          onClick={() => onLogOutcome?.()}
          disabled={disabled}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700 disabled:opacity-50"
        >
          <ClipboardCheck className="w-4 h-4" />
          Log Outcome
        </button>

        {/* Recipient info */}
        <div className="ml-auto text-xs text-gray-400">
          {mode === 'sms' && clientPhone && (
            <span>To: {clientPhone}</span>
          )}
          {mode === 'email' && clientEmail && (
            <span>To: {clientEmail}</span>
          )}
        </div>
      </div>

      {/* SMS Compose */}
      {mode === 'sms' && (
        <div className="px-4 pb-4">
          {/* Attachment Preview */}
          {smsAttachmentPreview && (
            <div className="mb-3 relative inline-block">
              <img
                src={smsAttachmentPreview}
                alt="Attachment preview"
                className="max-h-32 rounded-lg border border-gray-200"
              />
              <button
                onClick={handleRemoveAttachment}
                disabled={smsSending}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                           flex items-center justify-center hover:bg-red-600 shadow-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="flex gap-2">
            {/* Attachment Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || smsSending || uploadingAttachment}
              className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center
                         text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors self-end"
              title="Attach image (MMS)"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex-1 relative">
              <textarea
                ref={smsInputRef}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value.slice(0, SMS_CHAR_MAX))}
                onKeyDown={(e) => handleKeyDown(e, handleSendSMS)}
                placeholder={`Message ${clientName || 'client'}...`}
                disabled={disabled || smsSending}
                className="w-full resize-none rounded-2xl border border-gray-200 px-4 py-3 pr-12
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                           disabled:bg-gray-50 disabled:text-gray-400"
                rows={2}
              />
              {/* Character count */}
              <div className="absolute bottom-2 right-14 text-xs">
                <span className={smsMessage.length > SMS_CHAR_WARNING ? 'text-orange-500' : 'text-gray-400'}>
                  {smsMessage.length}/{SMS_CHAR_MAX}
                </span>
                {smsSegments > 1 && (
                  <span className="text-gray-400 ml-1">({smsSegments} msgs)</span>
                )}
              </div>
            </div>
            <button
              onClick={handleSendSMS}
              disabled={(!smsMessage.trim() && !smsAttachment) || smsSending || disabled}
              className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center
                         text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors self-end"
            >
              {smsSending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {uploadingAttachment ? 'Uploading image...' : 'Press Cmd+Enter to send • Click 📎 to attach image'}
          </p>
        </div>
      )}

      {/* Email Compose */}
      {mode === 'email' && (
        <div className="px-4 pb-4 space-y-2">
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Subject"
            disabled={disabled || emailSending}
            className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                       disabled:bg-gray-50"
          />

          {/* Formatting Toolbar */}
          <div className="flex items-center gap-1 p-1 bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={() => applyFormatting('bold')}
              disabled={disabled || emailSending}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Bold (Cmd+B)"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => applyFormatting('italic')}
              disabled={disabled || emailSending}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Italic (Cmd+I)"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => applyFormatting('underline')}
              disabled={disabled || emailSending}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Underline"
            >
              <Underline className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-gray-300 mx-1" />
            <button
              onClick={() => applyFormatting('link')}
              disabled={disabled || emailSending}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Insert Link"
            >
              <Link className="w-4 h-4" />
            </button>
            <button
              onClick={() => applyFormatting('list')}
              disabled={disabled || emailSending}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-gray-300 mx-1" />
            <button
              onClick={() => emailFileInputRef.current?.click()}
              disabled={disabled || emailSending || uploadingEmailAttachment}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
              title="Attach files"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              ref={emailFileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif"
              onChange={handleEmailFileSelect}
              className="hidden"
            />
            {uploadingEmailAttachment && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-1" />
            )}
          </div>

          <textarea
            ref={emailBodyRef}
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, handleSendEmail)}
            placeholder="Write your email..."
            disabled={disabled || emailSending}
            className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm
                       focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                       disabled:bg-gray-50 resize-none font-mono"
            rows={6}
          />

          {/* Email Attachments Preview */}
          {emailAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {emailAttachments.map((attachment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm"
                >
                  <Paperclip className="w-3 h-3 text-gray-500" />
                  <span className="truncate max-w-32">{attachment.name}</span>
                  <span className="text-gray-400 text-xs">({formatFileSize(attachment.size)})</span>
                  <button
                    onClick={() => handleRemoveEmailAttachment(index)}
                    disabled={emailSending}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Press Cmd+Enter to send • Use toolbar for formatting
            </p>
            <button
              onClick={handleSendEmail}
              disabled={!emailBody.trim() || emailSending || disabled}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white text-sm font-medium
                         rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
            >
              {emailSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
